import SEOHelmet from '@/components/SEOHelmet';
import { FC, useCallback } from 'react';
import styles from './users-management.module.scss';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg?react';
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { classnames } from '@/utils/functions';
import MsgParagraph from '@/components/MsgParagraph';
import { Role, User } from '@/models/user';
import GearIcon from '@/assets/icons/gear.svg?react';
import { AdminService } from '@/services/admin.service';
import Modal from '@/components/Modal/modal';
import NewUserForm from './components/NewUserForm';
import Button from '@/components/Button';
import useSession from '@/hooks/useSession';
import UpdateForm from './components/UpdateForm';
import SeatInfo from '@/components/SeatInfo';
import { toast } from 'react-toastify';
import { ZitadelUser, SeatInfoResponse, UserListResponse } from '@/models/sso';

const isZitadelUser = (user: User | ZitadelUser): user is ZitadelUser =>
  'zitadel_user_id' in user;

const UsersManagement = () => {
  const [query, setQuery] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [ssoUsers, setSsoUsers] = useState<ZitadelUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [sortDesc, setSortDesc] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<User | ZitadelUser | null>(
    null
  );
  const [sortColumn, setSortColumn] = useState<string>('email');
  const [openModal, setOpenModal] = useState<'new' | 'edit' | null>(null);
  const [seatInfo, setSeatInfo] = useState<SeatInfoResponse | null>(null);
  const [seatInfoLoading, setSeatInfoLoading] = useState<boolean>(true);
  const [userListResponse, setUserListResponse] =
    useState<UserListResponse | null>(null);
  const { session, orgIndex } = useSession();
  const { organizations } = session;
  const currentOrg = organizations[orgIndex] ?? organizations[0];

  // Use optional chaining to safely access org properties (guard is after hooks)
  const currentOrgID = currentOrg?.id ?? '';
  const isSsoOrg = currentOrg?.auth_method === 'sso';

  // Base columns (same for SSO and password orgs)
  // Status is rendered separately after the Edit column for SSO orgs
  const columns = [
    { label: 'Email', value: 'email' },
    { label: 'Name', value: 'name' },
    { label: 'Roles', value: 'roles' },
  ];

  const getAllUsers = useCallback(async () => {
    try {
      setLoading(true);

      if (isSsoOrg) {
        // For SSO orgs, get users from Zitadel with status
        const response = await AdminService.getOrgUsers(currentOrgID);
        setSsoUsers(response.users);
        setUserListResponse(response);
        // Clear legacy users state
        setUsers([]);
      } else {
        // For password orgs, get users from the users endpoint
        const fetchedUsers = await AdminService.getUsers(
          session.roles.includes(Role.SUPER_USER_ADMIN)
            ? currentOrgID
            : undefined
        );
        setUsers(fetchedUsers);
        // Clear SSO users state
        setSsoUsers([]);
        setUserListResponse(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrgID, isSsoOrg, session.roles]);

  const handleActivateUser = async (user: ZitadelUser) => {
    try {
      setActionLoading(user.zitadel_user_id);
      await AdminService.activateUser(currentOrgID, user.zitadel_user_id);
      // Refresh the list
      await getAllUsers();
      await getSeatInfo();
    } catch (err) {
      console.error('Failed to activate user:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to activate user'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivateUser = async (user: ZitadelUser) => {
    try {
      setActionLoading(user.zitadel_user_id);
      await AdminService.deactivateUser(currentOrgID, user.zitadel_user_id);
      // Refresh the list
      await getAllUsers();
      await getSeatInfo();
    } catch (err) {
      console.error('Failed to deactivate user:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to deactivate user'
      );
    } finally {
      setActionLoading(null);
    }
  };

  const getSeatInfo = useCallback(async () => {
    try {
      setSeatInfoLoading(true);
      const info = await AdminService.getSeatInfo(currentOrgID);
      setSeatInfo(info);
    } catch (err) {
      console.error('Failed to fetch seat info:', err);
    } finally {
      setSeatInfoLoading(false);
    }
  }, [currentOrgID]);

  const onSearch: (
    e: FormEvent<HTMLFormElement> | ChangeEvent<HTMLInputElement>
  ) => void = (e) => {
    let search = '';
    if (e.type === 'submit') {
      e.preventDefault();
      const formData = new FormData(
        (e as FormEvent<HTMLFormElement>).currentTarget
      );
      search = formData.get('search') as string;
    } else {
      search = (e as ChangeEvent<HTMLInputElement>).target.value;
    }

    setQuery(search);
  };

  const onSortingChange = (column: string) => {
    setSortColumn(column);
    if (sortColumn === column) {
      setSortDesc((current) => !current);
    } else {
      setSortDesc(true);
    }
  };

  const filteredUsers = useMemo<Array<User | ZitadelUser>>(() => {
    const source: Array<User | ZitadelUser> = isSsoOrg ? ssoUsers : users;
    const loweredQuery = query.toLowerCase();

    return source.filter(
      (user) =>
        user.email.toLowerCase().includes(loweredQuery) ||
        user.name.toLowerCase().includes(loweredQuery)
    );
  }, [isSsoOrg, query, ssoUsers, users]);

  const sortedUsers = useMemo<Array<User | ZitadelUser>>(() => {
    return [...filteredUsers].sort((a, b) => {
      if (
        isSsoOrg &&
        sortColumn === 'status' &&
        isZitadelUser(a) &&
        isZitadelUser(b)
      ) {
        const statusOrder = { active: 0, inactive: 1 };
        const result = statusOrder[a.status] - statusOrder[b.status];
        return sortDesc ? result : -result;
      }

      if (sortColumn === 'roles') {
        const aRoles = a.roles.join(',');
        const bRoles = b.roles.join(',');
        const result = aRoles.localeCompare(bRoles);
        return sortDesc ? result : -result;
      }

      const aVal = (a as unknown as Record<string, unknown>)[sortColumn];
      const bVal = (b as unknown as Record<string, unknown>)[sortColumn];

      if (aVal == null || bVal == null) {
        return 0;
      }

      let result = 0;
      if (aVal < bVal) {
        result = -1;
      } else if (aVal > bVal) {
        result = 1;
      }

      return sortDesc ? result : -result;
    });
  }, [filteredUsers, isSsoOrg, sortColumn, sortDesc]);

  // Total count for display
  const totalUserCount = isSsoOrg ? ssoUsers.length : users.length;
  const filteredCount = filteredUsers.length;

  useEffect(() => {
    getAllUsers();
    getSeatInfo();
  }, [getAllUsers, getSeatInfo, orgIndex]);

  // Guard against empty organizations array - must be after all hooks
  if (!currentOrg) {
    return (
      <div className={styles['users-management-wrapper']}>
        <SEOHelmet title="User Management" />
        <MsgParagraph>No organization available.</MsgParagraph>
      </div>
    );
  }

  return (
    <div className={styles['users-management-wrapper']}>
      <SEOHelmet title="User Management" />
      <h1>Users Management</h1>

      {loading ? (
        <MsgParagraph>Loading...</MsgParagraph>
      ) : (
        <>
          <Modal
            title="New User"
            isOpen={openModal === 'new'}
            onClose={() => setOpenModal(null)}
          >
            <NewUserForm
              onSubmit={() => {
                setOpenModal(null);
                getAllUsers();
                getSeatInfo();
              }}
            />
          </Modal>
          <Modal
            title="Edit Role"
            isOpen={openModal === 'edit'}
            onClose={() => {
              setOpenModal(null);
            }}
          >
            {selectedUser !== null && (
              <UpdateForm
                selectedUser={selectedUser}
                handleSubmit={() => {
                  setOpenModal(null);
                  getAllUsers();
                }}
                orgId={currentOrgID}
              />
            )}
          </Modal>
          <div className={styles['settings']}>
            <form onSubmit={onSearch}>
              <input
                type="search"
                id="search"
                name="search"
                placeholder="Type to filter by email or name.."
                onChange={onSearch}
                required
              />
            </form>
            {!isSsoOrg && (
              <Button
                variation="secondary"
                onClick={() => setOpenModal('new')}
                disabled={(seatInfo?.seats_available ?? 1) <= 0}
                title={
                  (seatInfo?.seats_available ?? 1) <= 0
                    ? 'Seat limit reached'
                    : undefined
                }
              >
                Add User
              </Button>
            )}
            {isSsoOrg && (
              <SeatInfo
                className={styles['seats-info']}
                seatsUsed={
                  isSsoOrg
                    ? (userListResponse?.active_count ?? 0)
                    : (seatInfo?.seats_used ?? 0)
                }
                seatLimit={
                  isSsoOrg
                    ? (userListResponse?.seat_limit ?? 20)
                    : (seatInfo?.seat_limit ?? 20)
                }
                loading={seatInfoLoading || loading}
              />
            )}
          </div>
          {query !== '' && filteredCount === 0 ? (
            <MsgParagraph>
              Search for<strong>"{query}"</strong> returned no results.
            </MsgParagraph>
          ) : (
            <>
              <p className={styles['counter']}>
                {query
                  ? `Filter matches ${filteredCount} of ${totalUserCount} Users`
                  : isSsoOrg && userListResponse
                    ? `${totalUserCount} Total Users (${userListResponse.active_count} active, ${userListResponse.inactive_count} inactive)`
                    : `${totalUserCount} Total Users`}
              </p>
              <div className={styles['table-wrapper']}>
                <table>
                  <thead>
                    <tr>
                      {columns.map(({ label, value }) => (
                        <th key={value}>
                          <SortingButton
                            column={value}
                            label={label}
                            currentColumn={sortColumn}
                            desc={sortDesc}
                            currentDesc={sortDesc}
                            onClick={() => onSortingChange(value)}
                          />
                        </th>
                      ))}
                      {isSsoOrg && (
                        <th>
                          <SortingButton
                            column="status"
                            label="Status"
                            currentColumn={sortColumn}
                            desc={sortDesc}
                            currentDesc={sortDesc}
                            onClick={() => onSortingChange('status')}
                          />
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user) => {
                      const { email, name, roles } = user;

                      const { zitadel_user_id, status } = user as ZitadelUser;

                      const isSsoUser = Object.prototype.hasOwnProperty.call(
                        user,
                        'zitadel_user_id'
                      );
                      const isActionLoading =
                        isSsoUser && actionLoading === zitadel_user_id;
                      const canActivate =
                        isSsoUser &&
                        status === 'inactive' &&
                        (userListResponse?.seats_available ?? 0) > 0;
                      return (
                        <tr
                          key={email}
                          id={email}
                          data-current={email === session.email}
                        >
                          <td className={styles['cell-email']}>
                            <p>
                              {email.split('@').map((part, index) => {
                                return (
                                  <span key={index}>
                                    {index === 1 && '@'}
                                    {part}
                                  </span>
                                );
                              })}
                            </p>
                          </td>
                          <td className={styles['cell-name']}>{name}</td>
                          <td className={styles['cell-roles']}>
                            <p>
                              {roles.map((role) => (
                                <span key={role} data-role={role}>
                                  {role.replace(/-/g, ' ')}
                                </span>
                              ))}
                              <button
                                type="button"
                                className={styles['edit-btn']}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedUser(user);
                                  setOpenModal('edit');
                                }}
                              >
                                <GearIcon />
                                Edit roles
                              </button>
                            </p>
                          </td>

                          {isSsoUser && (
                            <td className={styles['cell-actions']}>
                              {status === 'active' ? (
                                <Button
                                  variation="danger"
                                  className={styles['action-btn']}
                                  onClick={() =>
                                    handleDeactivateUser(user as ZitadelUser)
                                  }
                                  disabled={isActionLoading}
                                >
                                  {isActionLoading
                                    ? 'Processing...'
                                    : 'Deactivate'}
                                </Button>
                              ) : (
                                <Button
                                  className={styles['action-btn']}
                                  onClick={() =>
                                    handleActivateUser(user as ZitadelUser)
                                  }
                                  disabled={isActionLoading || !canActivate}
                                  title={
                                    !canActivate
                                      ? 'Seat limit reached'
                                      : undefined
                                  }
                                >
                                  {isActionLoading
                                    ? 'Processing...'
                                    : 'Activate'}
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

interface SortingButtonProps {
  column: string;
  label: string;
  currentColumn: string;
  desc: boolean;
  currentDesc: boolean;
  onClick: () => void;
}
const SortingButton: FC<SortingButtonProps> = ({
  column,
  label,
  currentColumn,
  currentDesc,
  onClick,
}) => {
  return (
    <button
      className={classnames(styles['sorting-button'], {
        [styles['active-col']]: currentColumn === column,
        [styles['is-asc']]: !currentDesc,
      })}
      onClick={onClick}
    >
      {label}

      <ChevronLeftIcon className={styles['sorting-order-icon']} />
    </button>
  );
};

export default UsersManagement;
